/**
 * Regeln zu Erfahrung und Level (Masterprompt Abschnitte 45 bis 47).
 *
 * Reine Funktionen ohne Zustand: Server und Client rechnen damit dasselbe aus.
 * Entschieden wird trotzdem ausschliesslich auf dem Server - der Client nutzt
 * die Funktionen nur fuer die Anzeige.
 */

import {
  BOSS_XP_BONUS,
  CAPSTONE_LEVEL,
  LEVEL_STAT_GROWTH,
  LEVEL_XP_THRESHOLDS,
  MAX_HERO_LEVEL,
  SKILL_POINT_LEVELS,
  TALENT_LEVEL,
  ULTIMATE_UNLOCK_LEVEL,
  XP_PER_LEVEL_ABOVE_10,
  XP_PER_TIER,
} from '@dueline/game-data';

/** Alles, was zur XP-Berechnung eines Creeps noetig ist. */
export interface XpCreep {
  tier: number;
  tags: readonly string[];
}

/** Wer den Creep erledigt hat - der Hero selbst oder etwas von ihm Bezahltes. */
export type KillSource = 'hero' | 'indirect';

/**
 * Erfahrung, die ein Creep beim Tod abwirft.
 *
 * Gestaffelt nach Tier, Bosse bekommen einen Aufschlag. Das Ergebnis wird
 * gerundet, damit im Spiel keine krummen Zahlen stehen.
 */
export function getCreepXp(creep: XpCreep): number {
  const basis = XP_PER_TIER[Math.min(Math.max(creep.tier, 1), XP_PER_TIER.length) - 1] ?? 0;
  const boss = creep.tags.includes('boss');
  return Math.round(basis * (boss ? 1 + BOSS_XP_BONUS : 1));
}

/*
 * Hier stand bis zum 09.10.2026 getAwardedXp: Was der Hero selbst erledigte,
 * zaehlte voll, was andere fuer ihn erledigten, zur Haelfte
 * (Produktentscheidung vom 23.09.2026).
 *
 * Die Regel ist weg, weil Abschnitt 5c sie ausdruecklich ueberholt hat:
 * Erfahrung gibt es fuer PRAESENZ an der Front, "unabhaengig davon, wer den
 * letzten Treffer gelandet hat". Auf der gemeinsamen Lane toeten ueberwiegend
 * Creeps einander - wer den letzten Treffer landet, kann niemand steuern.
 *
 * Was uebrig bleibt, ist getCreepXp: die volle Erfahrung. Wer dabei war,
 * bekommt sie; wer nicht, bekommt nichts (siehe praesenz.ts).
 */

/**
 * Gesamte Erfahrung, ab der dieses Level erreicht ist.
 *
 * Der Faktor streckt oder kuerzt die ganze Leiter (v0.48). 1 ist der
 * Normalfall; HERO_XP_FACTOR sagt, welcher Hero einen anderen hat und warum.
 */
export function getXpForLevel(level: number, factor = 1): number {
  const begrenzt = Math.min(Math.max(Math.floor(level), 1), MAX_HERO_LEVEL);
  const gespeichert = LEVEL_XP_THRESHOLDS[begrenzt - 1];
  if (gespeichert !== undefined) return Math.round(gespeichert * factor);

  const letzter = LEVEL_XP_THRESHOLDS[LEVEL_XP_THRESHOLDS.length - 1] ?? 0;
  return Math.round(
    (letzter + (begrenzt - LEVEL_XP_THRESHOLDS.length) * XP_PER_LEVEL_ABOVE_10) * factor,
  );
}

/** Level, das zu dieser Gesamterfahrung gehoert. */
export function getLevelForXp(xp: number, factor = 1): number {
  let level = 1;
  while (level < MAX_HERO_LEVEL && xp >= getXpForLevel(level + 1, factor)) level += 1;
  return level;
}

/** Fortschritt innerhalb des aktuellen Levels - fuer die Anzeige. */
export interface LevelProgress {
  level: number;
  /** Erfahrung seit dem Levelaufstieg. */
  intoLevel: number;
  /** Erfahrung, die dieses Level insgesamt braucht. 0 auf dem Hoechstlevel. */
  neededForLevel: number;
  /** Anteil 0 bis 1. Auf dem Hoechstlevel immer 1. */
  ratio: number;
  maxed: boolean;
}

export function getLevelProgress(xp: number, factor = 1): LevelProgress {
  const level = getLevelForXp(xp, factor);
  const basis = getXpForLevel(level, factor);

  if (level >= MAX_HERO_LEVEL) {
    return { level, intoLevel: 0, neededForLevel: 0, ratio: 1, maxed: true };
  }

  const naechste = getXpForLevel(level + 1, factor);
  const spanne = Math.max(1, naechste - basis);
  const drin = Math.max(0, xp - basis);

  return {
    level,
    intoLevel: drin,
    neededForLevel: spanne,
    ratio: Math.min(1, drin / spanne),
    maxed: false,
  };
}

/**
 * Faktor auf einen Grundwert fuer dieses Level.
 *
 * Level 1 laesst den Startwert unveraendert - der Zuwachs kommt erst mit dem
 * ersten Aufstieg.
 */
export function getLevelStatMultiplier(level: number, growth: number): number {
  const stufen = Math.max(0, Math.min(level, MAX_HERO_LEVEL) - 1);
  return 1 + stufen * growth;
}

export function getLevelMaxHpMultiplier(level: number): number {
  return getLevelStatMultiplier(level, LEVEL_STAT_GROWTH.maxHp);
}

export function getLevelMaxManaMultiplier(level: number): number {
  return getLevelStatMultiplier(level, LEVEL_STAT_GROWTH.maxMana);
}

export function getLevelAttackDamageMultiplier(level: number): number {
  return getLevelStatMultiplier(level, LEVEL_STAT_GROWTH.attackDamage);
}

/** Steht das Ultimate auf diesem Level schon zur Verfuegung? */
export function isUltimateUnlocked(level: number): boolean {
  return level >= ULTIMATE_UNLOCK_LEVEL;
}

/** Was ein einzelner Levelaufstieg bringt. */
export interface LevelRewards {
  /** Freie Skillpunkte, die dieses Level gibt. */
  skillPoints: number;
  /** Wird hier das Ultimate freigeschaltet? */
  ultimate: boolean;
  /** Wird hier ein Talent faellig? */
  talent: boolean;
  /** Wird hier der Capstone faellig? */
  capstone: boolean;
}

export function getLevelRewards(level: number): LevelRewards {
  return {
    skillPoints: SKILL_POINT_LEVELS.includes(level) ? 1 : 0,
    ultimate: level === ULTIMATE_UNLOCK_LEVEL,
    talent: level === TALENT_LEVEL,
    capstone: level === CAPSTONE_LEVEL,
  };
}
